import React, { useState, useEffect } from 'react';
import { supabase, getPuzzlePoolStatus, getPuzzlesByDifficulty, addPuzzle, deletePuzzle, updatePuzzleConfig, manuallyRotatePuzzles } from './supabaseClient';
import NavigationBar from './NavigationBar';
import HamburgerMenu from './HamburgerMenu';

function AdminPuzzlePage({ navigateTo, isLoggedIn, userProfile }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeSection, setActiveSection] = useState('puzzles');
  const [difficulty, setDifficulty] = useState('easy');
  const [puzzleGrid, setPuzzleGrid] = useState('');
  const [solutionGrid, setSolutionGrid] = useState('');
  const [notes, setNotes] = useState('');
  const [qualityRating, setQualityRating] = useState(3);
  const [puzzles, setPuzzles] = useState([]);
  const [poolStatus, setPoolStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isLoggedIn && userProfile?.is_admin) {
      fetchPoolStatus();
      fetchPuzzles();
      if (activeSection === 'feedback') {
        fetchFeedback();
      }
    }
  }, [difficulty, isLoggedIn, userProfile, activeSection]);

  const fetchPoolStatus = async () => {
    const status = await getPuzzlePoolStatus();
    setPoolStatus(status);
  };

  const fetchPuzzles = async () => {
    const data = await getPuzzlesByDifficulty(difficulty);
    setPuzzles(data);
  };

  const fetchFeedback = async () => {
    try {
      const { data, error } = await supabase
        .from('founder_feedback')
        .select(`
          *,
          profiles!inner(username, email, is_free_founder, is_paid_founder)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFeedbackList(data || []);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Parse grids from text input
      const puzzle = parseSudokuGrid(puzzleGrid);
      const solution = parseSudokuGrid(solutionGrid);

      // Validate that puzzle and solution are valid
      if (!validatePuzzleAndSolution(puzzle, solution)) {
        alert('Error: Puzzle and solution must be valid 9x9 Sudoku grids');
        setIsSubmitting(false);
        return;
      }

      const result = await addPuzzle({
        difficulty,
        puzzleGrid: puzzle,
        solutionGrid: solution,
        notes,
        qualityRating: qualityRating || null
      });

      if (result.success) {
        alert('Puzzle added successfully!');
        setPuzzleGrid('');
        setSolutionGrid('');
        setNotes('');
        setQualityRating(3);
        fetchPuzzles();
        fetchPoolStatus();
      } else {
        alert('Error adding puzzle: ' + result.error);
      }
    } catch (err) {
      alert('Error parsing grid: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const parseSudokuGrid = (text) => {
    // Replace dots with zeros, then remove all non-digit characters except newlines
    const withZeros = text.replace(/\./g, '0');
    const cleaned = withZeros.replace(/[^0-9\n]/g, '');
    const rows = cleaned.split('\n').filter(row => row.length > 0);
    
    if (rows.length !== 9) {
      throw new Error('Must have exactly 9 rows');
    }
    
    return rows.map(row => {
      const cells = row.split('').map(Number);
      if (cells.length !== 9) {
        throw new Error('Each row must have exactly 9 cells');
      }
      return cells;
    });
  };

  const validatePuzzleAndSolution = (puzzle, solution) => {
    // Check dimensions
    if (puzzle.length !== 9 || solution.length !== 9) return false;
    
    // Check each row has 9 cells
    for (let i = 0; i < 9; i++) {
      if (puzzle[i].length !== 9 || solution[i].length !== 9) return false;
    }
    
    // Check that given cells in puzzle match solution
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        if (puzzle[r][c] !== 0 && puzzle[r][c] !== solution[r][c]) {
          return false;
        }
      }
    }
    
    return true;
  };

  const formatGrid = (grid) => {
    if (!grid) return '';
    return grid.map(row => row.join('')).join('\n');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this puzzle? This cannot be undone.')) return;
    
    const result = await deletePuzzle(id);
    if (result.success) {
      alert('Puzzle deleted');
      fetchPuzzles();
      fetchPoolStatus();
    } else {
      alert('Error deleting puzzle: ' + result.error);
    }
  };

  const handleUpdateCooldown = async () => {
    const current = poolStatus?.cooldown_days || 120;
    const days = prompt('Enter new cooldown period (days):', current);
    if (!days) return;
    
    const parsed = parseInt(days);
    if (isNaN(parsed) || parsed < 1) {
      alert('Please enter a valid number');
      return;
    }
    
    const result = await updatePuzzleConfig('cooldown_days', parsed.toString());
    if (result.success) {
      alert(`Cooldown updated to ${parsed} days`);
      fetchPoolStatus();
    } else {
      alert('Error updating cooldown: ' + result.error);
    }
  };

  const handleManualRotate = async () => {
    if (!window.confirm('Manually trigger puzzle rotation?\n\nThis will:\n- Archive current leaderboard\n- Select new puzzles for today\n- Update daily_puzzles table\n\nContinue?')) {
      return;
    }
    
    const result = await manuallyRotatePuzzles();
    setShowRotateModal(false);
    
    if (result.success) {
      alert(`Rotation successful!\n\nArchived: ${result.data.archived_count} entries\n\nNew puzzles:\nEasy: ${result.data.puzzles.easy ? '✓' : '✗'}\nHard: ${result.data.puzzles.hard ? '✓' : '✗'}\nMaster: ${result.data.puzzles.master ? '✓' : '✗'}`);
      fetchPoolStatus();
    } else {
      alert('Error rotating puzzles: ' + result.error);
    }
  };

  // Require admin access
  if (!isLoggedIn || !userProfile?.is_admin) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        color: 'white',
        fontSize: '18px',
        fontWeight: '600',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <div>Admin access required</div>
          <button
            onClick={() => navigateTo('landing')}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              background: '#FF8C00',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '700'
            }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      padding: isMobile ? '16px' : '24px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {!isMobile && <NavigationBar currentPage="admin" isLoggedIn={isLoggedIn} navigateTo={navigateTo} />}
      {isMobile && <HamburgerMenu currentPage="admin" isLoggedIn={isLoggedIn} navigateTo={navigateTo} />}
      
      <div style={{ maxWidth: '1400px', margin: '0 auto', paddingTop: !isMobile ? '20px' : '0' }}>
        
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px',
          color: 'white'
        }}>
          <h1 style={{
            fontSize: isMobile ? '28px' : '36px',
            fontWeight: '800',
            margin: '0 0 8px 0'
          }}>
            Puzzle Admin Portal
          </h1>
          <p style={{
            fontSize: '16px',
            opacity: 0.85,
            margin: '0'
          }}>
            Manage daily puzzle library and rotation
          </p>
        </div>

        {/* Section Switcher */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          justifyContent: 'center'
        }}>
          <button
            onClick={() => setActiveSection('puzzles')}
            style={{
              padding: '12px 32px',
              background: activeSection === 'puzzles' ? '#FF8C00' : 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: activeSection === 'puzzles' ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Puzzle Management
          </button>
          <button
            onClick={() => setActiveSection('feedback')}
            style={{
              padding: '12px 32px',
              background: activeSection === 'feedback' ? '#FF8C00' : 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: activeSection === 'feedback' ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Founder Feedback
          </button>
        </div>

        {activeSection === 'puzzles' && (
        <div>
        {/* Pool Status Dashboard */}
        {poolStatus && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '24px' : '32px',
            marginBottom: '24px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '800',
                color: '#1F2937',
                margin: 0
              }}>
                Puzzle Pool Status
              </h2>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleUpdateCooldown}
                  style={{
                    padding: '8px 16px',
                    background: '#2563EB',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Change Cooldown ({poolStatus.cooldown_days} days)
                </button>
                <button
                  onClick={() => setShowRotateModal(true)}
                  style={{
                    padding: '8px 16px',
                    background: '#F59E0B',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Manual Rotate
                </button>
              </div>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '16px'
            }}>
              {['easy', 'hard', 'master'].map(diff => {
                const available = poolStatus[diff].never_used + poolStatus[diff].cooled_down;
                const isLow = available < 7;
                
                return (
                  <div
                    key={diff}
                    style={{
                      padding: '20px',
                      background: '#F9FAFB',
                      borderRadius: '12px',
                      border: isLow ? '2px solid #DC2626' : '1px solid #E5E7EB'
                    }}
                  >
                    <h3 style={{
                      textTransform: 'capitalize',
                      margin: '0 0 16px 0',
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#1F2937'
                    }}>
                      {diff}
                    </h3>
                    
                    <div style={{ fontSize: '14px', lineHeight: '2' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <strong>Total:</strong>
                        <span>{poolStatus[diff].total}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#059669' }}>
                        <strong>Never used:</strong>
                        <span>{poolStatus[diff].never_used}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#2563EB' }}>
                        <strong>Cooled down:</strong>
                        <span>{poolStatus[diff].cooled_down}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#DC2626' }}>
                        <strong>In cooldown:</strong>
                        <span>{poolStatus[diff].in_cooldown}</span>
                      </div>
                      <div style={{
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid #E5E7EB',
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontWeight: '700'
                      }}>
                        <strong>Available:</strong>
                        <span style={{ color: isLow ? '#DC2626' : '#059669' }}>
                          {available}
                        </span>
                      </div>
                    </div>
                    
                    {isLow && (
                      <div style={{
                        marginTop: '12px',
                        padding: '10px',
                        background: '#FEE2E2',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#DC2626',
                        fontWeight: '600',
                        textAlign: 'center'
                      }}>
                        ⚠️ Low pool! Add more puzzles
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add Puzzle Form */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '24px' : '32px',
          marginBottom: '24px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '800',
            color: '#1F2937',
            margin: '0 0 24px 0'
          }}>
            Add New Puzzle
          </h2>
          
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                Difficulty
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'inherit'
                }}
              >
                <option value="easy">Easy</option>
                <option value="hard">Hard</option>
                <option value="master">Master</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                Puzzle Grid (. for empty cells)
              </label>
              <textarea
                value={puzzleGrid}
                onChange={(e) => setPuzzleGrid(e.target.value)}
                placeholder={'53..7....\n6..195...\n.98....6.\n8...6...3\n4..8.3..1\n7...2...6\n.6....28.\n...419..5\n....8..79'}
                rows={9}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
                required
              />
              <small style={{ color: '#6B7280', fontSize: '13px' }}>
                Enter 9 rows of 9 characters (1-9 or .), one per line. Spaces optional.
              </small>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                Solution Grid (complete puzzle)
              </label>
              <textarea
                value={solutionGrid}
                onChange={(e) => setSolutionGrid(e.target.value)}
                placeholder={'534678912\n672195348\n198342567\n859761423\n426853791\n713924856\n961537284\n287419635\n345286179'}
                rows={9}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
                required
              />
              <small style={{ color: '#6B7280', fontSize: '13px' }}>
                Complete 9x9 solution. Must match given cells in puzzle.
              </small>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                  Quality Rating (1-5, optional)
                </label>
                <select
                  value={qualityRating}
                  onChange={(e) => setQualityRating(parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '15px'
                  }}
                >
                  <option value="">Not rated</option>
                  <option value="1">1 - Basic</option>
                  <option value="2">2 - Fair</option>
                  <option value="3">3 - Good</option>
                  <option value="4">4 - Great</option>
                  <option value="5">5 - Excellent</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                  Notes (optional)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Source, special features, etc."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '14px',
                background: isSubmitting ? '#9CA3AF' : '#FF8C00',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(255, 140, 0, 0.4)'
              }}
            >
              {isSubmitting ? 'Adding Puzzle...' : 'Add Puzzle to Library'}
            </button>
          </form>
        </div>

        {/* Puzzle Library */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '24px' : '32px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '800',
            color: '#1F2937',
            margin: '0 0 20px 0'
          }}>
            {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)} Puzzles ({puzzles.length})
          </h2>
          
          {puzzles.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '48px 24px',
              color: '#6B7280',
              fontSize: '15px'
            }}>
              No puzzles in library yet. Add your first puzzle above!
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700' }}>Added</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700' }}>Preview</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '700' }}>Used</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '700' }}>Times</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700' }}>Last Used</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700' }}>Notes</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '700' }}>Quality</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '700' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {puzzles.map(puzzle => (
                    <tr key={puzzle.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '12px' }}>
                        {new Date(puzzle.created_at).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: '2-digit'
                        })}
                      </td>
                      <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '11px' }}>
                        {puzzle.puzzle_grid[0].join('').substring(0, 9)}...
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {puzzle.times_used > 0 ? '✓' : '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {puzzle.times_used || 0}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {puzzle.last_used_date ? new Date(puzzle.last_used_date).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td style={{ padding: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {puzzle.notes || '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {puzzle.quality_rating ? '⭐'.repeat(puzzle.quality_rating) : '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleDelete(puzzle.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#DC2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Manual Rotate Modal */}
        {showRotateModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowRotateModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%'
            }}
          >
            <h2 style={{ fontSize: '24px', fontWeight: '800', margin: '0 0 16px 0' }}>
              Manual Puzzle Rotation
            </h2>
            <p style={{ fontSize: '15px', color: '#4B5563', lineHeight: '1.6', margin: '0 0 24px 0' }}>
              This will immediately:
            </p>
            <ul style={{ fontSize: '15px', color: '#4B5563', lineHeight: '1.8', margin: '0 0 24px 0', paddingLeft: '20px' }}>
              <li>Archive the current day's leaderboard</li>
              <li>Select new random puzzles (one per difficulty)</li>
              <li>Update the daily puzzles for today</li>
            </ul>
            <p style={{ fontSize: '14px', color: '#DC2626', fontWeight: '600', margin: '0 0 24px 0' }}>
              ⚠️ Only use this for testing. Normal rotation happens automatically at 00:00 UTC.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowRotateModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#F3F4F6',
                  color: '#4B5563',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleManualRotate}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Rotate Now
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
        )}

        {/* Founder Feedback Section */}
        {activeSection === 'feedback' && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '24px' : '32px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '800',
                color: '#1F2937',
                margin: '0'
              }}>
                Founder Feedback ({feedbackList.length})
              </h2>
              
              <select
                value={feedbackFilter}
                onChange={(e) => setFeedbackFilter(e.target.value)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Categories</option>
                <option value="UX">User Experience</option>
                <option value="Puzzle Quality">Puzzle Quality</option>
                <option value="Ideas">Feature Ideas</option>
                <option value="Bugs">Bugs</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {feedbackList
                .filter(f => feedbackFilter === 'all' || f.category === feedbackFilter)
                .map((feedback) => {
                  const getCategoryColor = (category) => {
                    const colors = {
                      'UX': { bg: '#EDE9FE', border: '#7C3AED', text: '#5B21B6' },
                      'Puzzle Quality': { bg: '#DBEAFE', border: '#2563EB', text: '#1E40AF' },
                      'Ideas': { bg: '#D1FAE5', border: '#10B981', text: '#065F46' },
                      'Bugs': { bg: '#FEE2E2', border: '#DC2626', text: '#991B1B' }
                    };
                    return colors[category] || { bg: '#F3F4F6', border: '#9CA3AF', text: '#4B5563' };
                  };

                  return (
                    <div
                      key={feedback.id}
                      style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        padding: '20px',
                        background: feedback.reviewed ? '#F9FAFB' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => setSelectedFeedback(selectedFeedback?.id === feedback.id ? null : feedback)}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '12px',
                        flexWrap: 'wrap',
                        gap: '8px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: '15px',
                            fontWeight: '700',
                            color: '#1F2937'
                          }}>
                            {feedback.profiles?.username || 'Unknown User'}
                          </span>
                          
                          {feedback.profiles?.is_paid_founder && (
                            <span style={{
                              padding: '4px 10px',
                              background: '#FEF3C7',
                              border: '1px solid #B58B2E',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#92400E'
                            }}>
                              Founding Member
                            </span>
                          )}
                          
                          {feedback.profiles?.is_free_founder && !feedback.profiles?.is_paid_founder && (
                            <span style={{
                              padding: '4px 10px',
                              background: '#DBEAFE',
                              border: '1px solid #2563EB',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#1E40AF'
                            }}>
                              Free Founder
                            </span>
                          )}
                          
                          <span style={{
                            padding: '4px 10px',
                            background: getCategoryColor(feedback.category).bg,
                            border: `1px solid ${getCategoryColor(feedback.category).border}`,
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: getCategoryColor(feedback.category).text
                          }}>
                            {feedback.category}
                          </span>
                        </div>
                        
                        <span style={{
                          fontSize: '13px',
                          color: '#6B7280'
                        }}>
                          {new Date(feedback.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>

                      <div style={{
                        fontSize: '14px',
                        color: '#4B5563',
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap',
                        maxHeight: selectedFeedback?.id === feedback.id ? 'none' : '60px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {feedback.feedback_text}
                      </div>

                      {feedback.feedback_text.length > 100 && selectedFeedback?.id !== feedback.id && (
                        <div style={{
                          fontSize: '13px',
                          color: '#FF8C00',
                          fontWeight: '600',
                          marginTop: '8px'
                        }}>
                          Click to read more...
                        </div>
                      )}

                      {selectedFeedback?.id === feedback.id && (
                        <div style={{
                          marginTop: '16px',
                          paddingTop: '16px',
                          borderTop: '1px solid #E5E7EB'
                        }}>
                          <div style={{ fontSize: '13px', color: '#6B7280', lineHeight: '1.8' }}>
                            <strong>Email:</strong> {feedback.profiles?.email || 'N/A'}<br />
                            <strong>User ID:</strong> {feedback.user_id}<br />
                            {feedback.reviewed && (
                              <>
                                <strong>Reviewed:</strong> {new Date(feedback.reviewed_at).toLocaleDateString('en-GB')}<br />
                                {feedback.admin_notes && (
                                  <>
                                    <strong>Admin Notes:</strong> {feedback.admin_notes}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              
              {feedbackList.filter(f => feedbackFilter === 'all' || f.category === feedbackFilter).length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#9CA3AF',
                  fontSize: '15px'
                }}>
                  No feedback found for this category.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPuzzlePage;
